import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Grid, Input, Option, Select, Typography, FormLabel, FormControl, Alert } from '@mui/joy';

import DynamicTable, { TableColumn } from '../../components/DynamicTable';
import { TextInputField } from '../../components/FormElements/TextInputField';
import GeneralModal from '../../components/GeneralModal';
import ResponsiveLayout from '../../components/layout/ResponsiveLayout';
import TableHeader from '../../components/TableHeader';
import { Device } from '../../models/Device/Device.model';
import DeviceSchema from '../../schema/deviceSchema';
import { DeviceTypeService } from '../../services/DeviceTypeService';
import { SystemDeviceService } from '../../services/SystemDeviceService';
import sessionStore from '../../stores/SessionStore';

interface DeviceFormInput {
  name?: string;
  device_type?: string | null;
  serial_number: string;
  device_uuid: string;
  description?: string;
  remote_id_number?: string;
  faa_reg_number?: string;
}

const DeviceList = () => {
  const { t } = useTranslation();
  const { user } = sessionStore;
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<Device[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'add' | 'edit' | 'delete'>('add');
  const [deviceTypeOptions, setDeviceTypeOptions] = useState<{ value: string; label: string }[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<DeviceFormInput>({
    resolver: yupResolver(DeviceSchema(t)),
    defaultValues: {
      name: '',
      device_type: null,
      serial_number: '',
      device_uuid: '',
      description: '',
      remote_id_number: '',
      faa_reg_number: ''
    }
  });

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const data = await SystemDeviceService.getDevices();
      setDevices(data || []);
    } catch (error: any) {
      toast.error(t('system_devices.errors.failed_to_fetch_devices') || 'Failed to fetch devices');
      setDevices([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDevices();

    // Fetch DeviceType objects and transform them for dropdown
    DeviceTypeService.getDeviceTypes()
      .then((response) => {
        if (response.success) {
          const options = response.data.map((deviceType) => ({
            value: deviceType.id,
            label: `${deviceType.name}${deviceType.model_number ? ` (${deviceType.model_number})` : ''}`
          }));
          setDeviceTypeOptions(options);
        } else {
          throw new Error();
        }
      })
      .catch(() => {
        toast.error(t('system_devices.errors.failed_to_fetch_device_types') || 'Failed to fetch device types');
      });
  }, []);

  const filteredDevices = (devices || []).filter(
    (device) =>
      (device.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (device.serial_number?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  const columns: TableColumn<Device, keyof Device>[] = [
    { header: 'Name', key: 'name' },
    {
      header: 'Device Type',
      key: 'device_type' as keyof Device,
      render: (value: any) => value?.name || '-'
    },
    { header: 'Serial Number', key: 'serial_number' },
    { header: 'Device UUID', key: 'device_uuid' },
    { header: 'FAA Reg', key: 'faa_reg_number' }
  ];

  const handleSelectionChange = (selected: Device[]) => {
    setSelectedRows(selected);
  };

  const handleOpenModal = (type: 'add' | 'edit' | 'delete') => {
    setModalType(type);
    if (type === 'edit' && selectedRows.length === 1) {
      const selected = selectedRows[0];
      reset({
        name: selected.name,
        device_type: selected.device_type?.id || null,
        serial_number: selected.serial_number,
        device_uuid: selected.device_uuid || '',
        description: selected.description,
        remote_id_number: selected.remote_id_number,
        faa_reg_number: selected.faa_reg_number
      });
    } else {
      reset({});
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const onSubmit = async (data: DeviceFormInput) => {
    const payload = { ...data };

    // Ensure device_type is never undefined - convert to null if undefined
    if (payload.device_type === undefined) {
      payload.device_type = null;
    }

    try {
      if (modalType === 'edit' && selectedRows.length === 1) {
        await SystemDeviceService.updateDevice(selectedRows[0].id, payload);
        toast.success(t('system_devices.messages.device_updated') || 'Device updated');
      } else {
        await SystemDeviceService.createDevice(payload);
        toast.success(t('system_devices.messages.device_created') || 'Device created');
      }
      fetchDevices();
      setIsModalOpen(false);
    } catch (error: any) {
      if (error?.status === 403) {
        toast.error(
          t('system_devices.errors.no_organization_access') || 'You do not have permission to perform this action'
        );
      } else {
        toast.error(t('system_devices.errors.failed_to_save') || 'Failed to save device');
      }
    }
  };

  const handleDelete = async () => {
    if (selectedRows.length === 1) {
      try {
        await SystemDeviceService.deleteDevice(selectedRows[0].id);
        toast.success(t('system_devices.messages.device_deleted') || 'Device deleted');
        fetchDevices();
        setIsModalOpen(false);
      } catch (error: any) {
        if (error?.status === 403) {
          toast.error(
            t('system_devices.errors.no_organization_access') || 'You do not have permission to delete this device'
          );
        } else {
          toast.error(t('system_devices.errors.failed_to_delete') || 'Failed to delete device');
        }
      }
    }
  };

  const modalContent =
    modalType === 'add' || modalType === 'edit' ? (
      <form id='device-form' onSubmit={handleSubmit(onSubmit)}>
        <Grid container>
          <Grid xs={12}>
            <Controller
              control={control}
              name='name'
              render={({ field }) => (
                <TextInputField label='Name' placeholder='Device Name' {...field} error={errors.name?.message} />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <FormControl className='mb-4'>
              <FormLabel>Device Type</FormLabel>
              <Controller
                control={control}
                name='device_type'
                render={({ field }) => (
                  <Select
                    value={field.value ?? ''}
                    onChange={(_, value) => field.onChange(value)}
                    placeholder='Select Device Type'
                    color={errors.device_type ? 'danger' : 'neutral'}
                  >
                    {deviceTypeOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.device_type && (
                <Typography color='danger' level='body-sm'>
                  {errors.device_type.message}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='serial_number'
              render={({ field }) => (
                <TextInputField
                  label='Serial Number'
                  placeholder='Serial Number'
                  required
                  {...field}
                  error={errors.serial_number?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='device_uuid'
              render={({ field }) => (
                <TextInputField
                  label='Device UUID'
                  placeholder='Device UUID'
                  required
                  {...field}
                  error={errors.serial_number?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='remote_id_number'
              render={({ field }) => (
                <TextInputField
                  label='Remote ID Number'
                  placeholder='Remote ID Number'
                  {...field}
                  error={errors.remote_id_number?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='faa_reg_number'
              render={({ field }) => (
                <TextInputField
                  label='FAA Reg Number'
                  placeholder='FAA Reg Number'
                  {...field}
                  error={errors.faa_reg_number?.message}
                />
              )}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              control={control}
              name='description'
              render={({ field }) => (
                <TextInputField
                  label='Description'
                  placeholder='Description'
                  {...field}
                  error={errors.description?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    ) : (
      <Typography>
        {t('system_devices.confirm_delete_msg') || 'Are you sure you want to delete this device?'}
      </Typography>
    );

  const modalActions =
    modalType === 'delete' ? (
      <>
        <Button color='neutral' onClick={handleCloseModal} variant='plain'>
          {t('general_button_labels.cancel')}
        </Button>
        <Button color='danger' onClick={handleDelete} variant='solid'>
          {t('general_button_labels.delete')}
        </Button>
      </>
    ) : (
      <>
        <Button color='neutral' onClick={handleCloseModal} variant='plain'>
          {t('general_button_labels.cancel')}
        </Button>
        <Button color='neutral' form='device-form' type='submit' variant='solid'>
          {t('general_button_labels.save')}
        </Button>
      </>
    );

  return (
    <ResponsiveLayout>
      <TableHeader
        title={
          selectedRows.length > 0
            ? `${selectedRows.length} ${selectedRows.length > 1 ? 'Devices' : 'Device'} Selected`
            : 'Devices'
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, ml: 2 }}>
          <Input
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search'
            startDecorator={<SearchIcon />}
            sx={{
              '--Input-minHeight': '2.5rem',
              maxWidth: '25rem',
              width: '100%'
            }}
            value={searchQuery}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
          {selectedRows.length === 0 &&
            user &&
            user.permissions &&
            user.permissions.some((perm) => ['view_create_devices'].includes(perm.name)) && (
              <Button color='neutral' onClick={() => handleOpenModal('add')}>
                {t('system_devices.buttons.create_device') || 'Add Device'}
              </Button>
            )}
          {selectedRows.length === 1 && (
            <>
              <Button color='neutral' onClick={() => handleOpenModal('edit')}>
                {t('system_devices.buttons.edit_device') || 'Edit'}
              </Button>
              <Button color='danger' onClick={() => handleOpenModal('delete')}>
                {t('general_button_labels.delete')}
              </Button>
            </>
          )}
          {selectedRows.length > 1 && (
            <Button color='danger' onClick={() => handleOpenModal('delete')}>
              {t('general_button_labels.delete')}
            </Button>
          )}
        </Box>
      </TableHeader>

      <DynamicTable
        columns={columns}
        data={filteredDevices}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        checkbox
      />

      <GeneralModal
        actions={modalActions}
        content={modalContent}
        onClose={handleCloseModal}
        open={isModalOpen}
        title={
          modalType === 'add'
            ? t('system_devices.buttons.create_device') || 'Add Device'
            : modalType === 'edit'
            ? t('system_devices.buttons.edit_device') || 'Edit Device'
            : t('general_button_labels.delete') || 'Delete'
        }
      />
    </ResponsiveLayout>
  );
};

export default DeviceList;
